import { useMemo, useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Table,
  Row,
  Col,
  Button,
  Badge,
  Label,
  FormFeedback,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"

import InputField from "components/Common/InputField"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useServiceInvoice } from "hooks/useServiceInvoice"
import { useAccountPaymentMethods } from "hooks/useAccountPaymentMethod"
import { useCarCareAccounts } from "hooks/useCarCareAccount"
import { createCarCareCustomerPayment } from "api/carCareCustomerPaymentApi"
import {
  getServiceInvoicePdfById,
  toggleServiceInvoicePreviousDue,
} from "api/serviceInvoiceApi"
import { formatReadableDate } from "utils/formatDate"
import { formatBDT } from "utils/formatPrice"

const CHARGE_BADGES = {
  chargeable: { label: "Chargeable", color: "secondary" },
  package: { label: "Package", color: "info" },
  free: { label: "Free", color: "success" },
}

const renderChargeBadge = chargeType => {
  const meta = CHARGE_BADGES[chargeType] || CHARGE_BADGES.chargeable
  return <Badge className={`badge-soft-${meta.color}`}>{meta.label}</Badge>
}

const ServiceInvoiceView = ({ invoiceId, isOpen, toggle, onChanged }) => {
  const { data: response, refetch } = useServiceInvoice(
    invoiceId,
    Boolean(invoiceId)
  )
  const invoice = response?.data || null

  const [showPay, setShowPay] = useState(false)
  const [pdfLoading, setPdfLoading] = useState(false)
  const [togglingDue, setTogglingDue] = useState(false)

  const handleTogglePreviousDue = async () => {
    setTogglingDue(true)
    try {
      await toggleServiceInvoicePreviousDue(invoice.id)
      await refetch()
    } catch (error) {
      console.error(error)
      toast.error("Failed to update previous-due setting.")
    } finally {
      setTogglingDue(false)
    }
  }

  const { data: methodsData } = useAccountPaymentMethods(
    { all: true, per_page: 200 },
    isOpen
  )
  const { data: accountsData } = useCarCareAccounts(
    { all: true, per_page: 200 },
    isOpen
  )

  const methodOptions = useMemo(() => {
    const list = methodsData?.data?.data || methodsData?.data || []
    return list.map(m => ({ value: m.id?.toString(), label: m.name }))
  }, [methodsData])

  const accountOptions = useMemo(() => {
    const list = accountsData?.data?.data || accountsData?.data || []
    return list.map(a => ({ value: a.id?.toString(), label: a.name }))
  }, [accountsData])

  const paymentFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      amount: "",
      payment_method_id: "",
      account_id: "",
      received_date: new Date().toISOString().slice(0, 10),
      reference_number: "",
      notes: "",
    },
    validationSchema: Yup.object({
      amount: Yup.number()
        .typeError("Enter an amount")
        .positive("Amount must be greater than 0")
        .required("Enter an amount"),
      payment_method_id: Yup.string().required("Select a payment method"),
      account_id: Yup.string().required("Select an account"),
      received_date: Yup.date().required("Select a date"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        await createCarCareCustomerPayment({
          customer_id: invoice.customer_id,
          service_invoice_id: invoice.id,
          amount: Number(values.amount),
          payment_method_id: values.payment_method_id,
          car_care_account_id: values.account_id,
          received_date: values.received_date,
          reference_number: values.reference_number || null,
          notes: values.notes || null,
        })
        toast.success("Payment recorded successfully.")
        resetForm()
        setShowPay(false)
        refetch()
        onChanged?.()
      } catch (error) {
        console.error(error)
        toast.error(
          error?.response?.data?.message ?? "Failed to record payment."
        )
      }
    },
  })

  if (!isOpen) return null

  const customer = invoice?.customer
  const customerInfo = invoice?.customer_info || {}
  const vehicleInfo = invoice?.vehicle_info || {}

  const pickCustomer = field =>
    customerInfo?.[field] || customer?.[field] || ""
  const pickVehicle = field =>
    vehicleInfo?.[field] || invoice?.vehicle?.[field] || ""

  const customerName = pickCustomer("name")
  const customerMobile = pickCustomer("mobile")
  const customerCuid = pickCustomer("cuid")
  const customerAddress = pickCustomer("full_address") || pickCustomer("address")
  const vehicleStockId = pickVehicle("stock_id")
  const vehicleChassis = pickVehicle("full_chassis")
  const vehicleEngine = pickVehicle("full_engine")
  const vehicleLabel = vehicleStockId || vehicleChassis || "-"
  const previousDue = Number(invoice?.previous_due || 0)
  const currentDue = Number(invoice?.current_due || 0)
  const totalDue = Number(
    invoice?.total_due ?? previousDue + currentDue
  )

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} className="border-0">
        <span className="fw-bold">
          Service Invoice {invoice?.invoice_no ? `— ${invoice.invoice_no}` : ""}
        </span>
      </ModalHeader>
      <ModalBody className="px-4">
        {!invoice ? (
          <div className="text-center text-muted py-4">Loading…</div>
        ) : (
          <>
            {/* Meta */}
            <Row className="mb-3">
              <Col md={6}>
                <div>
                  <strong>Customer:</strong> {customerName || "-"}
                  {customerMobile ? ` — ${customerMobile}` : ""}
                </div>
                {customerCuid ? (
                  <div className="text-muted small">{customerCuid}</div>
                ) : null}
                {customerAddress ? (
                  <div className="text-muted small">{customerAddress}</div>
                ) : null}
                <div>
                  <strong>Vehicle:</strong> {vehicleLabel}
                </div>
                {vehicleChassis ? (
                  <div className="text-muted small">Chassis: {vehicleChassis}</div>
                ) : null}
                {vehicleEngine ? (
                  <div className="text-muted small">Engine: {vehicleEngine}</div>
                ) : null}
              </Col>
              <Col md={6} className="text-md-end">
                <div>
                  <strong>Date:</strong>{" "}
                  {invoice.invoice_date
                    ? formatReadableDate(invoice.invoice_date)
                    : "-"}
                </div>
                <div>
                  <strong>Served By:</strong>{" "}
                  {invoice.served_by?.name || "-"}
                </div>
                <div>
                  <Badge
                    className={`badge-soft-${
                      invoice.bill_type === "package" ? "info" : "secondary"
                    }`}
                  >
                    {invoice.bill_type === "package" ? "Package" : "Itemized"}
                  </Badge>
                </div>
              </Col>
            </Row>

            {/* Items */}
            <Table bordered responsive className="align-middle">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th>Item</th>
                  <th>Type</th>
                  <th>Charge</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Unit Price</th>
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item, index) => (
                  <tr key={item.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.name}</td>
                    <td>
                      {item.item_type === "accessory"
                        ? "Accessory"
                        : "General Service"}
                    </td>
                    <td>{renderChargeBadge(item.charge_type)}</td>
                    <td className="text-end">{item.quantity}</td>
                    <td className="text-end">{formatBDT(item.unit_price)}</td>
                    <td className="text-end">{formatBDT(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Totals + Dues */}
            <Row className="justify-content-end">
              <Col md={6}>
                <Table borderless size="sm" className="mb-0">
                  <tbody>
                    {invoice.bill_type === "package" && (
                      <tr>
                        <td className="fw-semibold">Package Price</td>
                        <td className="text-end">
                          {formatBDT(invoice.package_price)}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td className="fw-semibold">Sub Total</td>
                      <td className="text-end">{formatBDT(invoice.subtotal)}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Discount</td>
                      <td className="text-end">
                        - {formatBDT(invoice.discount)}
                      </td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold">Invoice Total</td>
                      <td className="text-end fw-bold">
                        {formatBDT(invoice.total_amount)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Paid</td>
                      <td className="text-end">
                        {formatBDT(invoice.paid_amount)}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Previous Due</td>
                      <td className="text-end">{formatBDT(previousDue)}</td>
                    </tr>
                    <tr>
                      <td className="fw-semibold">Current Due</td>
                      <td className="text-end">{formatBDT(currentDue)}</td>
                    </tr>
                    <tr className="border-top">
                      <td className="fw-bold text-danger">Total Due</td>
                      <td className="text-end fw-bold text-danger">
                        {formatBDT(totalDue)}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>

            {/* Payment history */}
            <h6 className="mt-3 fw-semibold">Payments</h6>
            {invoice.payments?.length ? (
              <Table bordered responsive size="sm" className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th className="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map(p => (
                    <tr key={p.id}>
                      <td>
                        {p.received_date
                          ? formatReadableDate(p.received_date)
                          : "-"}
                      </td>
                      <td>{p.payment_method?.name || "-"}</td>
                      <td>{p.reference_number || "-"}</td>
                      <td className="text-end">{formatBDT(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <div className="text-muted small mb-2">
                No payments recorded yet.
              </div>
            )}

            {/* Payment form */}
            {showPay && (
              <div className="border rounded p-3 mb-3 bg-light">
                <Row className="g-2">
                  <Col md={4}>
                    <Label>Amount</Label>
                    <InputField
                      type="number"
                      name="amount"
                      value={paymentFormik.values.amount}
                      onChange={paymentFormik.handleChange}
                      onBlur={paymentFormik.handleBlur}
                      min="0"
                    />
                    {paymentFormik.touched.amount &&
                      paymentFormik.errors.amount && (
                        <FormFeedback type="invalid" className="d-block">
                          {paymentFormik.errors.amount}
                        </FormFeedback>
                      )}
                  </Col>
                  <Col md={4}>
                    <Label>Received Date</Label>
                    <InputField
                      type="date"
                      name="received_date"
                      value={paymentFormik.values.received_date}
                      onChange={paymentFormik.handleChange}
                    />
                  </Col>
                  <Col md={4}>
                    <Label>Reference</Label>
                    <InputField
                      type="text"
                      name="reference_number"
                      value={paymentFormik.values.reference_number}
                      onChange={paymentFormik.handleChange}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormikCreatableSelect
                      name="payment_method_id"
                      label="Payment Method"
                      formik={paymentFormik}
                      options={methodOptions}
                      placeholder="Select method"
                      required
                    />
                  </Col>
                  <Col md={6}>
                    <FormikCreatableSelect
                      name="account_id"
                      label="Account"
                      formik={paymentFormik}
                      options={accountOptions}
                      placeholder="Select account"
                      required
                    />
                  </Col>
                </Row>
                <div className="mb-2">
                  <Label>Notes</Label>
                  <InputField
                    type="textarea"
                    name="notes"
                    value={paymentFormik.values.notes}
                    onChange={paymentFormik.handleChange}
                  />
                </div>
                <div className="text-end">
                  <Button
                    color="light"
                    size="sm"
                    className="me-1"
                    onClick={() => setShowPay(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="success"
                    size="sm"
                    onClick={paymentFormik.handleSubmit}
                    disabled={paymentFormik.isSubmitting}
                  >
                    Save Payment
                  </Button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="text-end mt-3 d-flex justify-content-end flex-wrap gap-2">
              {!showPay && (
                <Button color="success" onClick={() => setShowPay(true)}>
                  <i className="mdi mdi-cash-plus me-1" />
                  Record Payment
                </Button>
              )}
              <Button
                color={invoice.show_previous_due ? "info" : "light"}
                outline={!invoice.show_previous_due}
                disabled={togglingDue}
                onClick={handleTogglePreviousDue}
                title="Toggle whether the customer's previous due prints on the invoice PDF"
              >
                <i className="mdi mdi-cash-multiple me-1" />
                Previous Due on PDF:{" "}
                {invoice.show_previous_due ? "Shown" : "Hidden"}
              </Button>
              <Button
                color="primary"
                outline
                disabled={pdfLoading}
                onClick={() =>
                  getServiceInvoicePdfById(invoice.id, setPdfLoading)
                }
              >
                {pdfLoading ? "Generating…" : "Download Invoice PDF"}
              </Button>
            </div>
          </>
        )}
      </ModalBody>
    </Modal>
  )
}

export default ServiceInvoiceView
